import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";

import { SovosGlobalLayout, SovosSnackbar } from "mosaic-react";

import { withRouter } from "react-router-dom";
import I18n from "../../I18n";
import { stringifyFilter } from "colbi_web_ui/lib/utils";
import { GlobalProvider as ColbiUIProvider } from "colbi_web_ui/lib/state/GlobalProvider";
import { GlobalProvider } from "../../state/globalProvider";
import { PersistGate } from "redux-persist/integration/react";
import { persistor } from "../../index";

import { removeModal } from "../../store/actions";

import { deepLink } from "../../utils/routingUtils";

import configsByTheme from "../../enums/configsByTheme";
import proxy from "../../routes/proxy";
import theme from "../../style/_theme.json";
import LanguageDialog from "../LanguageDialog/LanguageDialog";
import ModalController from "../../controllers/App/ModalController/ModalController";
import DialogController from "../../controllers/App/DialogController/DialogController";
import styles from "./GlobalLayout.module.sass";
import { AboutThisDialog } from "../AboutThisDialog/AboutThisDialog";
import { useAuth } from "react-oidc-context";

import * as detailPanels from "../DetailPanels/index";
import * as customCells from "../CustomCells/index";
import { getLocale } from "../../utils/locale";
import { buildSlotPropsForAsideMenu } from "./menuBuilderUtils";
import { SovosPlatformToolbar } from "mosaic-react-platform";
import PlatformContext from "mosaic-react-platform/internals/PlatformContext";
import { getConfigVariable } from "../../config/config";
import { getIdTokenFromStorage, isToDisplaySoviAiToolbar } from "./util";
import { SoviAiContext } from "../../contexts/SoviAiContext";
import { SOVI_OPEN_EVENT, handleSoviOpenEvent } from "../../utils/soviChatUtils";
import { hasModule } from "../../utils/utils";
import { initializePendo, updatePendoOptions, trackPageNavigation } from "../../utils/pendo";
import { sanitizeErrorForLogging } from "../../utils/utils";

const Provider = ({ children, ...props }) => {
    return (
        <GlobalProvider {...props}>
            <ColbiUIProvider {...props}>{children}</ColbiUIProvider>
        </GlobalProvider>
    );
};

const GlobalLayout = ({ children, ...props }) => {
    const {
        logout,
        pushModal,
        popModal,
        genericSearchAction,
        setLocale,
        match,
        location,
        history,
        reload,
        uploads,
        pushDialog,
        uploadFiles,
        download,
        submitAuditNote,
        setDataFileStatus,
        deleteFilePermanently,
        deleteInvalidFilePermanently,
        hideFile,
        downloadFile,
        reprocessRepository,
        auditNote,
        getFindingDetails,
        downloadJSON,
        submitTicket,
        reprocessFiles,
        navProps,
        downloadableQueries,
        params,
        repositoryStatus,
        getTotalExportLines,
        version,
        fetch,
        modules,
        getDownloadTemplateData,
        forceGenerateFile,
        getTranslationTableDatasetLocaleAndEntities,
        deleteTranslationTableAction,
        saveTranslationDetails,
        deleteTranslationDetailsRowAction,
        getColbiDdEntities,
        getTranslationBuilderCombinationsStats,
        applyRelatedEntitiesAction,
        organizationProjects,
        downloadCsvStreamAction,
        downloading,
    } = props;

    const {
        REACT_APP_COLBI_THEME,
        REACT_APP_LEARN_MORE_URL,
        REACT_APP_PRIVACY_POLICY_URL,
        REACT_APP_SSO_BASE_URL,
        REACT_APP_TERMS_AND_CONDITIONS_URL,
    } = window.saftEnvVariables;

    const appTheme = REACT_APP_COLBI_THEME ? REACT_APP_COLBI_THEME.toLocaleLowerCase() : "sovos";
    const localesByAppTheme = configsByTheme(appTheme)["locales"];

    const { links: staticLinks, userInfo: userInformation } = navProps;
    const auth = useAuth();

    const locale = getLocale(auth, match, true);

    const [userLocale, setUserLocale] = useState(locale);
    const [hasSoviAi, setHasSoviAi] = useState(null);
    const [soviContext, setSoviContext] = useState(undefined);
    const hasAskSovi = hasSoviAi && hasModule(modules, "ai_rules");

    const i18n = I18n.use(userLocale);
    const _selectedLink = location?.state?.selectedLink || i18n("overview");
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    const [isAboutThisDialogOpen, setIsAboutThisDialogOpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState(_selectedLink);
    const defaultNotification = { open: false, message: "", type: "", onClose: () => { } };
    const [notification, pushNotification] = useState({ ...defaultNotification });
    const pushNotificationHandler = (newNotification) => {
        pushNotification({ ...defaultNotification, ...newNotification });
    };

    useEffect(() => {
        if (location && location.state && location.state.selectedLink) {
            setSelectedLink(location.state.selectedLink);
        }
    }, [location, props]);

    useEffect(() => {
        deepLink(staticLinks, location.pathname, setSelectedLink);
    }, [staticLinks, location, setSelectedLink]);

    useEffect(() => {
        let isMounted = true;

        isToDisplaySoviAiToolbar(props.dispatch, props.user)
            .then((shouldDisplay) => {
                if (isMounted) {
                    setHasSoviAi(shouldDisplay);
                }
            })
            .catch((error) => {
                console.error("Error checking Sovi AI toolbar:", JSON.stringify(sanitizeErrorForLogging(error))); // eslint-disable-line no-console
                if (isMounted) {
                    setHasSoviAi(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [props.dispatch, props.user]);

    useEffect(() => {
        if (!hasSoviAi) return undefined;
        const onSoviOpen = (e) => {
            setSoviContext(e.detail?.context);
            handleSoviOpenEvent(e, () => setSoviContext(undefined));
        };
        window.addEventListener(SOVI_OPEN_EVENT, onSoviOpen);
        return () => window.removeEventListener(SOVI_OPEN_EVENT, onSoviOpen);
    }, [hasSoviAi]);

    const totalNestedLinks = useMemo(() => {
        const countNestedLinks = (links = []) =>
            links.reduce((total, item) => {
                const nestedLinks = Array.isArray(item?.nestedLinks) ? item.nestedLinks : [];
                return total + nestedLinks.length + countNestedLinks(nestedLinks);
            }, 0);
        return countNestedLinks(navProps?.links || []);
    }, [navProps]);

    const fullNavProps = {
        ...navProps,
        ...buildSlotPropsForAsideMenu(
            history,
            locale,
            userInformation,
            setIsLanguageModalOpen,
            logout,
            i18n,
            selectedLink,
            setIsAboutThisDialogOpen,
            auth,
        ),
        key: `nav-${totalNestedLinks}-total-nested-links`,
    };

    const appendViewModeToOverviewTarget = (url) => {
        if (!hasModule(modules, "new_navigation") || !url || url.includes("viewMode=")) {
            return url;
        }

        if (!/(master_data|transactional_data|structured_data)/.test(url)) {
            return url;
        }

        const viewMode =
            params?.viewMode || new URLSearchParams(location.search).get("viewMode");
        if (!viewMode) {
            return url;
        }

        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}viewMode=${viewMode}`;
    };

    const goto = (page, filters, anchor) => {
        const pageUrl = page
            ? (typeof page.join === "function" ? page.join("/") : page).replace(/\/+/g, "/")
            : null;
        const urlFilters = filters
            ? Array.isArray(filters)
                ? stringifyFilter(filters)
                : stringifyFilter([filters])
            : "";
        const localePrefixed = pageUrl
            ? `${locale ? `/${locale}${!pageUrl.match(/^\//) ? "/" : ""}` : ""}`
            : null;
        let gotoPage = pageUrl
            ? `${localePrefixed}${pageUrl}${urlFilters.length ? `?filter=${urlFilters}&resetFilters=${Date.now()}` : ""
            }`
            : `${location.pathname}?filter=${urlFilters}&resetFilters=${Date.now()}${anchor ? `#${anchor}` : ""
            }`;
        gotoPage = appendViewModeToOverviewTarget(gotoPage);
        history.push(gotoPage);
    };
    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const userInformationRef = useRef(userInformation);
    const organizationProjectsRef = useRef(organizationProjects);
    const authRef = useRef(auth);
    const paramsRef = useRef(params);
    const pendoInitializedRef = useRef(false);
    const previousPathRef = useRef(null);

    userInformationRef.current = userInformation;
    organizationProjectsRef.current = organizationProjects;
    authRef.current = auth;
    paramsRef.current = params;

    useEffect(() => {
        if (userInformation?.email && !pendoInitializedRef.current) {
            initializePendo({
                userInformation: userInformationRef.current,
                organizationProjects: organizationProjectsRef.current,
                auth: authRef.current,
            });
            pendoInitializedRef.current = true;
        }
    }, [userInformation?.email]);

    useEffect(() => {
        if (userInformation?.email && organizationProjects?.id) {
            updatePendoOptions({
                userInformation: userInformationRef.current,
                organizationProjects: organizationProjectsRef.current,
                auth: authRef.current,
            });
        }
    }, [userInformation?.email, organizationProjects?.id]);

    useEffect(() => {
        const currentPath = location.pathname;
        const previousPath = previousPathRef.current;

        if (previousPath === null) {
            previousPathRef.current = currentPath;
            return;
        }

        if (previousPath !== currentPath) {
            trackPageNavigation(previousPath, currentPath, {
                organization_id: organizationProjectsRef.current?.id || "",
                project_id: paramsRef.current?.projectId || "",
            });
            previousPathRef.current = currentPath;
        }
    }, [location.pathname]);

    return (
        <Provider
            user={userInformation}
            proxy={proxy}
            theme={theme}
            uploads={uploads}
            modules={modules}
            actions={{
                pushModal,
                pushDialog,
                popModal,
                uploadFiles,
                submitTicket,
                download,
                submitAuditNote,
                removeModal,
                setDataFileStatus,
                deleteFilePermanently,
                deleteInvalidFilePermanently,
                hideFile,
                downloadFile,
                reprocessRepository,
                logout,
                getFindingDetails,
                downloadJSON,
                setLocale: (locale) => {
                    history.push(`/${locale}${history.location.pathname.substring(3)}`);
                },
                search: genericSearchAction,
                goto,
                downloadableQueries,
                reprocessFiles,
                getDownloadTemplateData,
                forceGenerateFile,
                getTranslationTableDatasetLocaleAndEntities,
                deleteTranslationTableAction,
                saveTranslationDetails,
                deleteTranslationDetailsRowAction,
                getColbiDdEntities,
                getTranslationBuilderCombinationsStats,
                applyRelatedEntitiesAction,
                downloadCsvStreamAction,
                downloading,
            }}
            i18n={i18n}
            locale={locale || localesByAppTheme[0].id}
            locales={localesByAppTheme}
            reload={reload}
            params={{
                ...params,
                fiscalYear: params.fiscalYear,
                importId: params.importId,
            }}
            history={history}
            location={location}
            repositoryStatus={repositoryStatus}
            auditNote={auditNote}
            totalLinesCsv={getTotalExportLines || ""}
            customCardsCells={customCells}
            detailPanelComponents={detailPanels}
            pushNotification={pushNotificationHandler}
            fetch={fetch}
        >
            <PersistGate persistor={persistor}>
                <SovosGlobalLayout
                    className={styles["global-layout"]}
                    NavigationProps={fullNavProps}
                >
                    <SoviAiContext.Provider value={{ hasSoviAi, hasAskSovi }}>
                        {children}
                    </SoviAiContext.Provider>
                </SovosGlobalLayout>
                {/* TODO: After toolbar are responsive with page layout remove in colbi ui specific code to give 56px of right margin */}
                {hasSoviAi && (
                    <PlatformContext.Provider value={{ aiContext: soviContext }}>
                        <SovosPlatformToolbar
                            productSuite="Indirect Tax"
                            pageContext={{
                                pageTitle: document?.title,
                                accountId: params?.projectId,
                                userId: userInformation?.email,
                                userLanguage: locale,
                            }}
                            aiApiBase={getConfigVariable("REACT_APP_AI_API_BASE")}
                            applicationId={"saft"}
                            organizationId={organizationProjects?.id || ""}
                            getAiRequestHeaders={() => {
                                const idToken = getIdTokenFromStorage();
                                return idToken ? { Authorization: `Bearer ${idToken}` } : {};
                            }}
                            docsApiEndpoint={getConfigVariable("REACT_APP_DOCS_API_ENDPOINT")}
                            getDocsRequestHeaders={() => {
                                const idToken = getIdTokenFromStorage();
                                return idToken ? { Authorization: `Bearer ${idToken}` } : {};
                            }}
                            docsOrigin={"indirect-tax/indirect-tax-products/data-and-analytics/saf-t"}
                            onError={(e) =>
                                pushNotification({
                                    open: true,
                                    message: e.message,
                                    type: "error",
                                    timeout: "short",
                                })
                            }
                        />
                    </PlatformContext.Provider>
                )}
                <AboutThisDialog
                    isOpen={isAboutThisDialogOpen}
                    setIsAboutThisDialogOpen={setIsAboutThisDialogOpen || (() => { })}
                    productName={"SAF-T"}
                    localesByAppTheme={localesByAppTheme}
                    i18n={i18n}
                    learnMoreUrl={REACT_APP_LEARN_MORE_URL}
                    sovosLinkURL={`${REACT_APP_SSO_BASE_URL}/my-profile/`}
                    privacyPolicyUrl={REACT_APP_PRIVACY_POLICY_URL}
                    termsAndConditionsUrl={REACT_APP_TERMS_AND_CONDITIONS_URL}
                    version={version}
                />
                <LanguageDialog
                    isOpen={isLanguageModalOpen}
                    setIsOpen={setIsLanguageModalOpen || (() => { })}
                    i18n={i18n}
                    setUserLocale={setUserLocale}
                    userLocale={userLocale}
                    localesByAppTheme={localesByAppTheme}
                    setLocale={setLocale}
                    history={history}
                />

                <ModalController popModal={popModal} />
                <DialogController i18n={i18n} />
                <SovosSnackbar
                    onClose={() => {
                        notification?.onClose();
                        pushNotification({
                            ...notification,
                            open: false,
                        });
                    }}
                    message={notification?.message}
                    open={notification?.open}
                    variant={notification?.type ? notification.type : "confirmation"}
                    timeout={notification?.timeout ? notification?.timeout : "medium"}
                    disableWindowBlurListener
                />
                <SovosSnackbar
                    message={i18n`Generating export... This may take a few minutes.`}
                    onClose={() => { }}
                    open={downloading}
                    variant="confirmation"
                    timeout="long"
                    disableWindowBlurListener
                />
            </PersistGate>
        </Provider>
    );
};

GlobalLayout.queries = (args, userModulesByProject) => {
    return [
        {
            resource: "get_organizations_cache_by_user",
            body: "modules",
            forceRefresh: true,
        },
    ];
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote, downloading }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
        downloading,
    }),
    {
        removeModal,
        detailPanels,
    },
)(withRouter(GlobalLayout));
